// src/components/dashboard components/forms/ProjectAboutForm.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Save, Pencil } from "lucide-react";

// ✅ Use environment variable for API base
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ProjectAboutForm = ({
  apiBase = `${API_BASE_URL}/projects`, // ✅ Default to env-based API
  token = localStorage.getItem("token"),
}) => {
  const [formData, setFormData] = useState({
    project_about_id: 0,
    title: "",
    subtitle: "",
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // ===============================
  // FETCH ALL (ADMIN GET)
  // ===============================
  const fetchAbout = async () => {
    try {
      // ✅ Updated to use env variable
      const res = await axios.get(`${apiBase}/admin/about`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("res", res);

      const data = Array.isArray(res.data) ? res.data : [];
      setRows(data);

      // Prefill form with latest row
      if (data.length > 0) {
        const latest = data[0];
        setFormData({
          project_about_id: latest.project_about_id,
          title: latest.title || "",
          subtitle: latest.subtitle || "",
        });
      } else {
        setFormData({
          project_about_id: 0,
          title: "",
          subtitle: "",
        });
      }
    } catch (err) {
      console.error("Error fetching project about:", err);
      alert("Failed to load project about data.");
    }
  };

  useEffect(() => {
    fetchAbout();
  }, []);

  // ===============================
  // HANDLE CHANGE
  // ===============================
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ===============================
  // HANDLE EDIT (from table)
  // ===============================
  const handleEdit = (row) => {
    setFormData({
      project_about_id: row.project_about_id,
      title: row.title || "",
      subtitle: row.subtitle || "",
    });
  };

  // ===============================
  // SAVE (ADMIN POST)
  // ===============================
  const handleSave = async () => {
    if (!formData.title.trim()) {
      alert("Title is required.");
      return;
    }

    try {
      setLoading(true);

      // ✅ Updated API endpoint with env base
      await axios.post(
        `${apiBase}/admin/about/save`,
        {
          project_about_id: formData.project_about_id || 0,
          title: formData.title,
          subtitle: formData.subtitle,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      alert("Project About saved successfully.");
      await fetchAbout();
    } catch (err) {
      console.error("Error saving project about:", err);
      alert("Failed to save. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  // ===============================
  // RENDER
  // ===============================
  return (
    <section className="bg-white rounded-xl shadow p-8 mb-10">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-blue-700">
          Projects About Section
        </h2>
        <button
          onClick={handleSave}
          disabled={loading}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-70"
        >
          <Save size={18} />
          {loading ? "Saving..." : "Save"}
        </button>
      </div>

      {/* Existing Rows Table */}
      <div className="overflow-x-auto mb-8">
        <table className="min-w-full border border-gray-200 text-sm rounded-lg overflow-hidden">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="px-4 py-3 text-left font-semibold">No.</th>
              <th className="px-4 py-3 text-left font-semibold">Title</th>
              <th className="px-4 py-3 text-left font-semibold">Subtitle</th>
              <th className="px-4 py-3 text-center font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows.map((row, idx) => (
                <tr
                  key={row.project_about_id}
                  className="border-t hover:bg-gray-50 transition"
                >
                  <td className="px-4 py-3">{idx + 1}</td>
                  <td className="px-4 py-3">{row.title}</td>
                  <td className="px-4 py-3">{row.subtitle}</td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => handleEdit(row)}
                      className="text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                    >
                      <Pencil size={16} /> Edit
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-5 text-center text-gray-500 italic"
                >
                  No project about content added yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Form */}
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <input
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none"
            placeholder="e.g., About Our Projects"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Subtitle
          </label>
          <input
            name="subtitle"
            value={formData.subtitle}
            onChange={handleChange}
            className="w-full border rounded-lg px-3 py-2 shadow-sm focus:ring-2 focus:ring-blue-400 outline-none"
            placeholder="e.g., HMDA and RERA backed developments across South India"
          />
        </div>
      </div>
    </section>
  );
};

export default ProjectAboutForm;
